(() => {
  const COLOUR_OPTION_NAMES = ['color', 'colour'];

  const normalize = (value) =>
    String(value || '')
      .trim()
      .toLowerCase();

  function getOptionName(control) {
    const name = normalize(control.getAttribute('name'));

    const nameMatch = name.match(/options?\[([^\]]+)\]/);
    if (nameMatch) return normalize(nameMatch[1]);

    const fieldset = control.closest('fieldset');
    const legend = fieldset?.querySelector('legend');

    if (legend) {
      return normalize(
        legend.textContent
          .replace(/selected.*$/i, '')
          .replace(/:.*$/i, '')
      );
    }

    const wrapper = control.closest(
      '.product-form__input, .variant-option, [data-option-name]'
    );

    if (wrapper?.dataset.optionName) {
      return normalize(wrapper.dataset.optionName);
    }

    const label = wrapper?.querySelector(
      'label, .variant-option__label'
    );

    return normalize(label?.textContent);
  }

  function getSelectedColour(productRoot) {
    const controls = productRoot.querySelectorAll(
      [
        'input[type="radio"]:checked',
        'input[type="checkbox"]:checked',
        'select[name*="options"]',
        'select[data-option-name]',
      ].join(',')
    );

    for (const control of controls) {
      const optionName = getOptionName(control);

      const isColourOption = COLOUR_OPTION_NAMES.some(
        (name) =>
          optionName === name ||
          optionName.includes(name)
      );

      if (!isColourOption) continue;

      return normalize(control.value);
    }

    return '';
  }

  function getProductRoot(gallery) {
    return (
      gallery.closest(
        '[data-section-type*="product"], .shopify-section'
      ) || document
    );
  }

  function setMediaVisibility(item, visible) {
    item.hidden = !visible;
    item.classList.toggle(
      'variant-media-filter-hidden',
      !visible
    );

    item.setAttribute(
      'aria-hidden',
      visible ? 'false' : 'true'
    );
  }

  function activateFirstVisibleSlide(gallery) {
    const firstVisibleSlide = gallery.querySelector(
      'slideshow-slide[data-media-colour]:not([hidden])'
    );

    if (!firstVisibleSlide) return;

    const currentVisibleSlide = gallery.querySelector(
      'slideshow-slide[aria-hidden="false"]:not([hidden])'
    );

    if (currentVisibleSlide) return;

    const slideIndex =
      firstVisibleSlide.getAttribute('slide-index') ||
      firstVisibleSlide.dataset.index;

    const slideshow =
      gallery.querySelector('slideshow-component');

    /*
     * Different theme versions expose different methods.
     * Try the available method without breaking the page.
     */
    if (
      slideshow &&
      typeof slideshow.select === 'function'
    ) {
      slideshow.select(Number(slideIndex || 0));
      return;
    }

    if (
      slideshow &&
      typeof slideshow.goToSlide === 'function'
    ) {
      slideshow.goToSlide(Number(slideIndex || 0));
      return;
    }

    firstVisibleSlide.scrollIntoView({
      block: 'nearest',
      inline: 'start',
      behavior: 'auto',
    });
  }

  function filterGallery(gallery) {
    const productRoot = getProductRoot(gallery);
    const selectedColour =
      getSelectedColour(productRoot);

    if (!selectedColour) return;

    const mediaItems = Array.from(
      gallery.querySelectorAll(
        [
          'slideshow-slide[data-media-colour]',
          '.media-gallery__grid > [data-media-colour]',
        ].join(',')
      )
    );

    if (!mediaItems.length) return;

    const matchingItems = mediaItems.filter(
      (item) =>
        normalize(item.dataset.mediaColour) ===
        selectedColour
    );

    /*
     * Fail safely: if no image alt text matches the
     * selected colour, leave the gallery unfiltered.
     */
    if (!matchingItems.length) {
      mediaItems.forEach((item) =>
        setMediaVisibility(item, true)
      );

      return;
    }

    mediaItems.forEach((item) => {
      const mediaColour = normalize(
        item.dataset.mediaColour
      );

      setMediaVisibility(
        item,
        mediaColour === selectedColour
      );
    });

    activateFirstVisibleSlide(gallery);

    gallery.dispatchEvent(
      new CustomEvent('variant-media-filter:updated', {
        bubbles: true,
        detail: {
          colour: selectedColour,
          visibleCount: matchingItems.length,
        },
      })
    );
  }

  function filterAllGalleries(root = document) {
    root
      .querySelectorAll('media-gallery')
      .forEach(filterGallery);
  }

  function initialize() {
    filterAllGalleries();

    /*
     * Capturing catches changes from standard Shopify
     * inputs and custom variant-picker components.
     */
    document.addEventListener(
      'change',
      (event) => {
        const target = event.target;

        if (
          !(target instanceof HTMLInputElement) &&
          !(target instanceof HTMLSelectElement)
        ) {
          return;
        }

        window.requestAnimationFrame(() => {
          const productRoot =
            target.closest(
              '[data-section-type*="product"], .shopify-section'
            ) || document;

          filterAllGalleries(productRoot);
        });
      },
      true
    );

    /*
     * The theme may replace product markup after a
     * variant change. Reapply filtering when that happens.
     */
    const observer = new MutationObserver(
      (mutations) => {
        const galleryWasAdded = mutations.some(
          (mutation) =>
            Array.from(mutation.addedNodes).some(
              (node) =>
                node instanceof Element &&
                (node.matches?.('media-gallery') ||
                  node.querySelector?.('media-gallery'))
            )
        );

        if (galleryWasAdded) {
          window.requestAnimationFrame(() => {
            filterAllGalleries();
          });
        }
      }
    );

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    document.addEventListener(
      'shopify:section:load',
      (event) => {
        filterAllGalleries(event.target);
      }
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initialize
    );
  } else {
    initialize();
  }
})();